import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { setUnsavedChangesGuard } from "../../../component/student/unsavedChangesGate"

const GUARD_KEY = "__learnerUnsavedGuard"

const isModifiedClick = (event: MouseEvent) =>
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  event.button !== 0

const readState = (): Record<string, unknown> => {
  const state = window.history.state
  return state && typeof state === "object" ? { ...state } : {}
}

const hasGuardState = () => readState()[GUARD_KEY] === 1

const pushGuardState = () => {
  if (hasGuardState()) return
  window.history.pushState({ ...readState(), [GUARD_KEY]: 1 }, "")
}

const clearGuardState = () => {
  if (!hasGuardState()) return
  const next = readState()
  delete next[GUARD_KEY]
  window.history.replaceState(next, "")
}

/**
 * Warn before leaving a dirty form: reload/close, in-app links, search,
 * browser back, and any caller-driven leave via `requestLeave`.
 */
const useUnsavedChangesGuard = (isDirty: boolean) => {
  const navigate = useNavigate()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const dirtyRef = useRef(isDirty)
  const bypassRef = useRef(false)
  const pendingRef = useRef<(() => void) | null>(null)
  dirtyRef.current = isDirty

  const requestLeave = useCallback((onConfirm: () => void) => {
    if (!dirtyRef.current) {
      onConfirm()
      return
    }
    pendingRef.current = onConfirm
    setIsConfirmOpen(true)
  }, [])

  const requestLeaveRef = useRef(requestLeave)
  requestLeaveRef.current = requestLeave

  useEffect(() => {
    setUnsavedChangesGuard({
      isDirty: () => dirtyRef.current,
      requestLeave: (onConfirm) => requestLeaveRef.current(onConfirm),
    })
    return () => setUnsavedChangesGuard(null)
  }, [])

  const confirmLeave = useCallback(() => {
    const next = pendingRef.current
    pendingRef.current = null
    setIsConfirmOpen(false)
    next?.()
  }, [])

  const stay = useCallback(() => {
    pendingRef.current = null
    setIsConfirmOpen(false)
  }, [])

  useEffect(() => {
    if (!isDirty) return undefined
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [isDirty])

  useEffect(() => {
    if (!isDirty) return undefined
    bypassRef.current = false
    pushGuardState()

    const onPopState = () => {
      if (bypassRef.current || !dirtyRef.current) return
      pushGuardState()
      requestLeaveRef.current(() => {
        bypassRef.current = true
        dirtyRef.current = false
        window.history.go(-2)
      })
    }
    window.addEventListener("popstate", onPopState)
    return () => {
      window.removeEventListener("popstate", onPopState)
      if (bypassRef.current) return
      if (hasGuardState()) window.history.back()
    }
  }, [isDirty])

  useEffect(() => {
    if (!isDirty) return undefined

    const onClick = (event: MouseEvent) => {
      if (isModifiedClick(event)) return
      const target = event.target as HTMLElement | null
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null
      if (!anchor) return
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return
      const href = anchor.getAttribute("href")
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return
      }
      const url = new URL(anchor.href, window.location.href)
      if (url.origin !== window.location.origin) return
      const current = `${window.location.pathname}${window.location.search}`
      const next = `${url.pathname}${url.search}`
      if (current === next) return
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      requestLeaveRef.current(() => {
        bypassRef.current = true
        dirtyRef.current = false
        clearGuardState()
        navigate(`${url.pathname}${url.search}${url.hash}`)
      })
    }

    const onSubmit = (event: Event) => {
      if (bypassRef.current) {
        bypassRef.current = false
        return
      }
      const form = event.target
      if (!(form instanceof HTMLFormElement)) return
      if (form.closest(".note-composer")) return
      if (!form.closest(".student-search-field")) return
      event.preventDefault()
      event.stopPropagation()
      requestLeaveRef.current(() => {
        bypassRef.current = true
        dirtyRef.current = false
        clearGuardState()
        form.requestSubmit()
      })
    }

    document.addEventListener("click", onClick, true)
    document.addEventListener("submit", onSubmit, true)
    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("submit", onSubmit, true)
    }
  }, [isDirty, navigate])

  return {
    isConfirmOpen,
    requestLeave,
    confirmLeave,
    stay,
  }
}

export default useUnsavedChangesGuard
